import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { FormField } from '../components/ui/FormField';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { Alert } from '../components/ui/Alert';
import { ChangePasswordModal } from '../components/profile/ChangePasswordModal';
import { LawEnforcementProfile as LawEnforcementProfileComponent } from '../components/profile/LawEnforcementProfile';
import { AccountTypeCard } from '../components/profile/AccountTypeCard';
import LawEnforcementProfileModal from '../components/profile/LawEnforcementProfileModal';
import LawEnforcementProfileView from '../components/profile/LawEnforcementProfileView';
import { formatDate } from '../utils/dateFormatter';
import { LawEnforcementProfile } from '../types';

export const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [lawEnforcementProfile, setLawEnforcementProfile] = useState<LawEnforcementProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showLawEnforcementModal, setShowLawEnforcementModal] = useState(false);
  const [lawEnforcementModalMode, setLawEnforcementModalMode] = useState<'view' | 'edit'>('view');
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  // Update form data when user changes
  React.useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || ''
      });
    }
  }, [user]);

  // Load law enforcement profile (always attempt so we can reflect approved state even if userType isn't updated yet)
  useEffect(() => {
    const loadLawEnforcementProfile = async () => {
      if (!user) return;
      setIsLoadingProfile(true);
      try {
        const profile = await apiService.getLawEnforcementProfile();
        setLawEnforcementProfile(profile);
      } catch (err) {
        // Profile might not exist yet, that's okay
        console.log('No law enforcement profile found');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadLawEnforcementProfile();
  }, [user?.id]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }
    
    if (formData.phoneNumber && !/^\+?1?\d{9,15}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoadingStates(prev => ({ ...prev, 'profile-update': true }));
    setError(null);
    setSuccess(null);

    try {
      await apiService.updateUserProfile(formData);
      await refreshUser();
      setSuccess('Profile updated successfully');
      setFormErrors({});
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, 'profile-update': false }));
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDismissError = () => setError(null);
  const handleDismissSuccess = () => setSuccess(null);


  const handleUserTypeChange = async (userType: 'regular' | 'law_enforcement') => {
    // Only allow switching to law enforcement, not back to regular
    if (userType === 'law_enforcement' && user?.userType !== 'law_enforcement') {
      try {
        setIsLoadingProfile(true);
        setError(null);
        
        // First try to load existing profile
        try {
          const existingProfile = await apiService.getLawEnforcementProfile();
          setLawEnforcementProfile(existingProfile);
          setSuccess('Law enforcement profile loaded successfully! Opening verification form...');
          // Automatically open the edit modal to complete verification
          handleOpenLawEnforcementModal('edit');
        } catch (loadErr: any) {
          // If profile doesn't exist, create a new one
          if (loadErr.response?.status === 404 || loadErr.message?.includes('not found')) {
            const profile = await apiService.createLawEnforcementProfile();
            setLawEnforcementProfile(profile);
            await refreshUser(); // Refresh user to get updated userType
            setSuccess('Law enforcement profile created successfully! Opening verification form...');
            // Automatically open the edit modal to complete verification
            handleOpenLawEnforcementModal('edit');
          } else {
            throw loadErr; // Re-throw if it's a different error
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to create law enforcement profile');
      } finally {
        setIsLoadingProfile(false);
      }
    }
  };

  const handleOpenLawEnforcementModal = (mode: 'view' | 'edit' = 'view') => {
    setLawEnforcementModalMode(mode);
    setShowLawEnforcementModal(true);
  };

  const handleCloseLawEnforcementModal = () => {
    setShowLawEnforcementModal(false);
  };

  const handleLawEnforcementProfileUpdate = async (updatedProfile: LawEnforcementProfile) => {
    setLawEnforcementProfile(updatedProfile);
    // Refresh user data to get updated user type
    await refreshUser();
  };

  const refreshLawEnforcementProfile = async () => {
    if (user?.userType === 'law_enforcement') {
      try {
        const profile = await apiService.getLawEnforcementProfile();
        setLawEnforcementProfile(profile);
      } catch (err) {
        console.log('No law enforcement profile found');
      }
    }
  };

  // Determine effective account type based on approved law enforcement profile
  const isLawEnforcementApproved = !!lawEnforcementProfile && (
    lawEnforcementProfile.verificationStatus === 'approved' ||
    lawEnforcementProfile.verificationStatus === 'auto_approved'
  );
  const effectiveUserType: 'regular' | 'law_enforcement' = isLawEnforcementApproved
    ? 'law_enforcement'
    : (user?.userType ?? 'regular');

  const getVerificationStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Profile"
        subtitle="Manage your account information and preferences."
        align="center"
      />

      {/* Status Messages */}
      {error && (
        <Alert
          type="error"
          message={error}
          dismissible
          onDismiss={handleDismissError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          dismissible
          onDismiss={handleDismissSuccess}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        {/* Main Content */}
        <div className="xl:col-span-2 space-y-4 sm:space-y-6">
          {/* Account Type Card (show law enforcement if approved, even if backend userType hasn't updated yet) */}
          <AccountTypeCard
            userType={effectiveUserType}
            lawEnforcementProfile={lawEnforcementProfile}
            isLoadingProfile={isLoadingProfile}
            onUpgrade={() => handleUserTypeChange('law_enforcement')}
          />

          {/* Profile Form */}
          <Card>
            <div className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Account Information</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <FormField
                    label="First Name"
                    htmlFor="firstName"
                    required
                    error={formErrors.firstName}
                    helpText="Enter your legal first name"
                  >
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="Enter your first name"
                      disabled={loadingStates['profile-update']}
                      aria-describedby={formErrors.firstName ? 'firstName-error' : 'firstName-help'}
                      aria-invalid={!!formErrors.firstName}
                    />
                  </FormField>

                  <FormField
                    label="Last Name"
                    htmlFor="lastName"
                    required
                    error={formErrors.lastName}
                    helpText="Enter your legal last name"
                  >
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="Enter your last name"
                      disabled={loadingStates['profile-update']}
                      aria-describedby={formErrors.lastName ? 'lastName-error' : 'lastName-help'}
                      aria-invalid={!!formErrors.lastName}
                    />
                  </FormField>
                </div>

                <FormField
                  label="Email Address"
                  htmlFor="email"
                  helpText="Email address cannot be changed"
                >
                  <input
                    type="email"
                    id="email"
                    value={user.email}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    aria-describedby="email-help"
                  />
                </FormField>

                <FormField
                  label="Phone Number"
                  htmlFor="phoneNumber"
                  error={formErrors.phoneNumber}
                  helpText="Optional: Enter your phone number with country code"
                >
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Enter your phone number (e.g., +1234567890)"
                    disabled={loadingStates['profile-update']}
                    aria-describedby={formErrors.phoneNumber ? 'phoneNumber-error' : 'phoneNumber-help'}
                    aria-invalid={!!formErrors.phoneNumber}
                  />
                </FormField>

                <FormField
                  label="Verification Status"
                  htmlFor="verificationStatus"
                >
                  <div className="flex items-center">
                    <Badge variant={getVerificationStatusColor(user.verificationStatus)}>
                      {user.verificationStatus}
                    </Badge>
                  </div>
                </FormField>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full min-h-[44px] touch-manipulation"
                    loading={loadingStates['profile-update']}
                    disabled={loadingStates['profile-update']}
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    }
                  >
                    {loadingStates['profile-update'] ? 'Updating...' : 'Update Profile'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>

          {/* Security Section */}
          <Card>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Security</h2>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg gap-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Password</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {user?.isGoogleUser && !user?.passwordSet
                        ? 'Set a password to enable direct login to your account'
                        : 'Change your password to keep your account secure'
                      }
                    </p>
                    {user?.isGoogleUser && (
                      <div className="flex items-center mt-2">
                        <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span className="text-xs text-blue-600 dark:text-blue-400">
                          {user.passwordSet ? 'Google + Password' : 'Google Account'}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => setShowPasswordModal(true)}
                    className="self-start sm:self-center"
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    }
                  >
                    {user?.isGoogleUser && !user?.passwordSet ? 'Set Password' : 'Change Password'}
                  </Button>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg gap-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Account Type</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {user?.isGoogleUser 
                        ? 'Your account is linked to Google authentication' 
                        : 'Standard account with email and password authentication'
                      }
                    </p>
                  </div>
                  <div className="flex items-center self-start sm:self-center">
                    {user?.isGoogleUser ? (
                      <Badge variant="info">Google Account</Badge>
                    ) : (
                      <Badge variant="default">Standard Account</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Law Enforcement Profile Section (visible when approved or explicitly law_enforcement) */}
          {(effectiveUserType === 'law_enforcement') && (
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Law Enforcement Profile</h2>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => handleOpenLawEnforcementModal('view')}
                      disabled={isLoadingProfile}
                    >
                      View Profile
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => handleOpenLawEnforcementModal('edit')}
                      disabled={isLoadingProfile}
                    >
                      Edit Profile
                    </Button>
                  </div>
                </div>
                
                {isLoadingProfile ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner text="Loading law enforcement profile..." />
                  </div>
                ) : lawEnforcementProfile ? (
                  <div className="space-y-4">
                    <LawEnforcementProfileView
                      profile={lawEnforcementProfile}
                      onEdit={() => handleOpenLawEnforcementModal('edit')}
                      showEditButton={false}
                    />
                    <div className="text-center">
                      <Button
                        variant="primary"
                        onClick={() => handleOpenLawEnforcementModal('edit')}
                        className="w-full"
                      >
                        Complete Verification Process
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      No law enforcement profile found. Create one to get started.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => handleOpenLawEnforcementModal('edit')}
                    >
                      Create Profile
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-1 space-y-4 sm:space-y-6">
          {/* Account Statistics */}
          <Card>
            <div className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Account Statistics</h2>
              
              <div className="space-y-4">
                <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                    {user.intCoinsBalance.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Int Coins</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {formatDate(user.createdAt)}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Member Since</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex justify-center mb-2">
                    <Badge variant={getVerificationStatusColor(user.verificationStatus)}>
                      {user.verificationStatus}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Status</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start min-h-[44px] touch-manipulation"
                  onClick={() => setShowPasswordModal(true)}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                >
                  {user?.isGoogleUser && !user?.passwordSet ? 'Set Password' : 'Change Password'}
                </Button>
                
                {user.userType === 'law_enforcement' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start min-h-[44px] touch-manipulation"
                    onClick={() => handleOpenLawEnforcementModal('view')}
                    disabled={isLoadingProfile}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    }
                  >
                    View Law Enforcement Profile
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        isGoogleUser={user?.isGoogleUser}
        passwordSet={user?.passwordSet}
      />


      {/* Law Enforcement Profile Modal */}
      <LawEnforcementProfileModal
        isOpen={showLawEnforcementModal}
        onClose={handleCloseLawEnforcementModal}
        profile={lawEnforcementProfile || undefined}
        onProfileUpdate={handleLawEnforcementProfileUpdate}
        mode={lawEnforcementModalMode}
      />
    </div>
  );
}; 